/**
 * Lead enums - SOURCE OF TRUTH
 * Pure TypeScript - no Drizzle imports
 */
// Lead pipeline stages — SOURCE OF TRUTH for the UI vocabulary and for write
// validation. The ordered funnel:
//   new → contacted → qualified → booked | lost
// A lead is `contacted` once we reach out, `qualified` once they reply, and
// `booked` once they book or pay (see `converted_at`). `booked` is the point a
// person stops being a lead and becomes a client — the Customers tabs split on
// it (see `leadStageGroups`). `lost` stays a valid status for display, but the
// stage dropdown only offers the four pipeline stages (see
// `pipelineLeadStatusValues`).
export const leadStatusLabels = {
    new: 'Incoming Lead',
    contacted: 'Contacted',
    qualified: 'Qualified',
    booked: 'Booked',
    lost: 'Lost',
};
export const leadStatusValues = Object.keys(leadStatusLabels);
// The stages the Clients stage dropdown offers, in funnel order. Excludes
// `lost` — a lead is marked lost from the row menu, not this picker — so the
// picker reads as a clean four-step pipeline.
export const pipelineLeadStatusValues = [
    'new',
    'contacted',
    'qualified',
    'booked',
];
// Retired stage values from the old sales-funnel vocabulary. Retained ONLY so
// the pg `lead_status` enum keeps them — dropping a pg enum value forces a type
// rewrite (see .claude/rules/database/drizzle-orm.md). They are never shown,
// never a valid write target (update-lead rejects them), and the Phase 1
// backfill migration maps every existing row off them:
//   proposal | negotiation → contacted,  won → booked,  cold → lost
export const retiredLeadStatusValues = [
    'proposal',
    'negotiation',
    'won',
    'cold',
];
// The FULL enum membership in the EXACT order the pg `lead_status` enum was
// originally created. The Drizzle pgEnum MUST derive from this, not from
// `leadStatusValues`, or a `drizzle-kit generate` would try to drop the retired
// values and fail. Order = original creation order — do not reorder.
export const allLeadStatusValues = [
    'new',
    'contacted',
    'qualified',
    'proposal',
    'negotiation',
    'won',
    'lost',
    'booked',
    'cold',
];
// Customers tab → the derived stages it shows. Two axes, kept apart: a person
// is a LEAD until their first booking or paid sale and a CLIENT from then on
// (`converted_at`, never un-stamped); the funnel stages new / contacted /
// qualified only subdivide the Leads tab (`leadFunnelStageValues`). The "All"
// tab applies no stage filter and is handled by the list service, so it is
// absent here. Tabs never surface retired values.
//
// `contacted` and `booked` are the tab values of the pre-split strip
// (All / Leads / Contacted / Booked). No tab shows them any more, but they stay
// valid so saved links and already-shipped app bundles keep working.
export const leadStageGroups = {
    leads: ['new', 'contacted', 'qualified'],
    clients: ['booked'],
    lost: ['lost'],
    contacted: ['contacted', 'qualified'],
    booked: ['booked'],
};
export const leadStageGroupValues = [
    'all',
    'leads',
    'clients',
    'lost',
    'contacted',
    'booked',
];
/** The tabs the Customers surface shows, in order. */
export const leadStageTabValues = [
    'all',
    'leads',
    'clients',
    'lost',
];
/** The funnel stages a lead moves through before converting — the Leads tab's sub-filter. */
export const leadFunnelStageValues = [
    'new',
    'contacted',
    'qualified',
];
// Collapse any stored status (including retired sales-funnel values still in
// the pg enum) onto an active stage. proposal/negotiation are retired synonyms
// for contacted, won for booked, cold for lost; every current stage — including
// the revived `qualified` — passes straight through.
export function normalizeLeadStage(status) {
    switch (status) {
        case 'proposal':
        case 'negotiation':
            return 'contacted';
        case 'won':
            return 'booked';
        case 'cold':
            return 'lost';
        default:
            return status;
    }
}
/** Display label for any stored status, retired values included. */
export function leadStatusLabel(status) {
    return leadStatusLabels[normalizeLeadStage(status)];
}
// Lead source labels
export const leadSourceLabels = {
    facebook: 'Facebook',
    instagram: 'Instagram',
    whatsapp: 'WhatsApp',
    sms: 'SMS',
    phone: 'Phone call',
    website: 'Website',
    manual: 'Manual',
    referral: 'Referral',
    other: 'Other',
    meta_lead_form: 'Meta Lead Form',
};
export const leadSourceValues = Object.keys(leadSourceLabels);
// The Leads tab's "where did they come from" strip, in display order. Derived
// from `lead.source` (see `derivedLeadSourceTab`) with two splits the raw
// column cannot make on its own:
//   meta_lead_form          → meta_ads
//   manual + a CSV import   → imported (`consent_source = 'csv_import'`)
//   manual otherwise        → team (typed in by staff)
// Imports had been landing under "Manual", indistinguishable from someone the
// front desk entered by hand.
export const leadSourceTabLabels = {
    meta_ads: 'Lead forms',
    instagram: 'Instagram',
    whatsapp: 'WhatsApp',
    facebook: 'Facebook',
    sms: 'SMS',
    phone: 'Phone calls',
    website: 'Website',
    referral: 'Referral',
    team: 'Added by team',
    imported: 'Imported',
    other: 'Other',
};
export const leadSourceTabValues = Object.keys(leadSourceTabLabels);
/**
 * One row's source bucket — the TypeScript twin of the SQL
 * `derivedLeadSourceTab` in the features package. Keep the two in step: the
 * badge on a row must name the tab that row is counted under.
 */
export function leadSourceTabOf(source, consentSource) {
    if (source === 'meta_lead_form')
        return 'meta_ads';
    if (source === 'manual') {
        return consentSource === 'csv_import' ? 'imported' : 'team';
    }
    return source;
}
// Consent source labels
export const consentSourceLabels = {
    meta_form: 'Meta Lead Form',
    manual_entry: 'Manual Entry',
    csv_import: 'CSV Import',
    grandfathered: 'Grandfathered',
    user_update: 'User Update',
    booking_form: 'Booking Form',
    incoming_message: 'Incoming Message',
    web_opt_in: 'Web Opt-In Form',
};
export const consentSourceValues = Object.keys(consentSourceLabels);
/** Capture sources kept for audit. Channel boxes govern all sends. */
export const leadMarketingBasisLabels = {
    none: 'No marketing consent',
    meta_form: 'Meta form',
    explicit_opt_in: 'Explicit opt-in',
};
export const leadMarketingBasisValues = Object.keys(leadMarketingBasisLabels);
export const leadConsentEventSourceValues = [
    'meta_form',
    'booking_checkbox',
    'funnel_continue',
    'opt_in_page',
    'stop_keyword',
    'start_keyword',
    'unsubscribe_link',
    'plain_language_optout',
    'staff_edit',
    // The clinic's own authorisation: the import box, and leads counted as
    // opted in at the consent rollout.
    'import_authorization',
    'rollout_backfill',
];
//# sourceMappingURL=leads.js.map